import { FC } from 'react';
import { Modal, TextField } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import { ClaimStatus } from 'store/claims';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { Stack } from '@mui/material';

interface Values {
  comment: string;
}

const INITIAL_VALUES: Values = {
  comment: '',
};

interface Props extends ModalProps {
  title: string;
  action: 'request' | 'reject';
  closeModal: (
    param?:
      | { action: 'CLOSE' }
      | { action: 'REJECT'; payload: { comment: string; status: ClaimStatus } },
  ) => void;
}

export const ClaimRejectModal: FC<Props> = ({ title, action, closeModal }) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { isDirty, errors },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  const onSubmit: SubmitHandler<Values> = (values) => {
    closeModal({
      action: 'REJECT',
      payload: {
        comment: values.comment,
        status: action === 'reject' ? 'rejected' : 'pendingEmployeeAction',
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={title}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          ...(action === 'reject'
            ? [
                {
                  variant: 'danger-outline',
                  label: t('global.actions.reject'),
                  type: 'submit',
                  disabled: !isDirty,
                } as const,
              ]
            : []),
          ...(action === 'request'
            ? [
                {
                  variant: 'secondary',
                  label: t('claims.actions.request_extra_input'),
                  type: 'submit',
                  disabled: !isDirty,
                } as const,
              ]
            : []),
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="comment"
            control={control}
            rules={{
              minLength: {
                value: 10,
                message: t('common.minLengthValidationError', {
                  minLength: 10,
                }),
              },
            }}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <TextField
                  label={t('claims.form.comment.label')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors.comment?.message}
                  required
                  ref={ref}
                />
              );
            }}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
